import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Icon } from '@livable/saas-react-components';
import { Button, InputBase } from '@mui/material';
import React, { useState } from 'react';
import { formatUtilityLabel } from 'src/pages/NewBillingModule/ApplyBillingFull/BillAutomationExtraction';
import { AccordionHeader } from './AccordionHeader';
import useStyles from './billDetailsStyles';
import { DASH, formatMoney, getServiceTotal, getServiceUsage, pickValue } from './billDetailsUtils';
import { addLineItem, removeLineItem, updateLineItem, updateService } from './billEditUtils';
import { UtilityRail } from './UtilityRail';

/**
 * One property of the bill: utilities on the rail, the selected utility's line items in the table.
 * `serviceIndexes` says which of the property's services belong here, since the ones the property is
 * not set up for are shown on their own; edits still address a service by its place in the bill.
 */
export function PropertyLineItems(_ref) {
  var _data$properties, _pickValue, _pickValue2;
  let currency = _ref.currency,
    data = _ref.data,
    _ref$defaultExpanded = _ref.defaultExpanded,
    defaultExpanded = _ref$defaultExpanded === void 0 ? false : _ref$defaultExpanded,
    glCodeByServiceType = _ref.glCodeByServiceType,
    _ref$isEditing = _ref.isEditing,
    isEditing = _ref$isEditing === void 0 ? false : _ref$isEditing,
    onChange = _ref.onChange,
    propertyIndex = _ref.propertyIndex,
    serviceIndexes = _ref.serviceIndexes,
    _ref$showGlCode = _ref.showGlCode,
    showGlCode = _ref$showGlCode === void 0 ? false : _ref$showGlCode;
  const classes = useStyles();
  const _useState = useState(defaultExpanded),
    _useState2 = _slicedToArray(_useState, 2),
    isExpanded = _useState2[0],
    setIsExpanded = _useState2[1];
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    activePosition = _useState4[0],
    setActivePosition = _useState4[1];
  const property = (data === null || data === void 0 || (_data$properties = data.properties) === null || _data$properties === void 0 ? void 0 : _data$properties[propertyIndex]) || {};
  const allServices = property.services || [];
  const indexes = serviceIndexes || allServices.map((item, index) => index);
  const services = indexes.map(index => allServices[index]).filter(Boolean);
  const safePosition = services.length ? Math.min(activePosition, services.length - 1) : 0;
  const serviceIndex = indexes[safePosition];
  const service = services[safePosition];
  const lineItems = (service === null || service === void 0 ? void 0 : service.lineItems) || [];
  const usage = getServiceUsage(service);
  // The GL code belongs to the utility, so it is entered once in the summary and only read here.
  const glCode = (glCodeByServiceType === null || glCodeByServiceType === void 0 ? void 0 : glCodeByServiceType[service === null || service === void 0 ? void 0 : service.serviceType]) || '';
  const patchLine = (lineIndex, patch) => onChange(updateLineItem(data, propertyIndex, serviceIndex, lineIndex, patch));
  return /*#__PURE__*/React.createElement("div", {
    className: classes.accordion
  }, /*#__PURE__*/React.createElement(AccordionHeader, {
    isExpanded: isExpanded,
    onToggle: () => setIsExpanded(current => !current),
    title: property.address || DASH
  }), isExpanded ? /*#__PURE__*/React.createElement("div", {
    className: classes.accordionBody
  }, services.length > 0 ? /*#__PURE__*/React.createElement(UtilityRail, {
    activeIndex: safePosition,
    onChange: setActivePosition,
    utilities: services
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: classes.utilityPanel
  }, services.length === 0 ? /*#__PURE__*/React.createElement("div", {
    className: classes.emptyState
  }, "No line items were extracted for this property.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.lineItemsHead
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.lineItemName
  }, formatUtilityLabel(service.serviceType), " line item"), showGlCode ? /*#__PURE__*/React.createElement("span", {
    className: classes.lineItemGlCode
  }, "Gl code") : null, /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.lineItemAmount, " ").concat(isEditing ? classes.lineItemEditAmount : '')
  }, "Amount"), isEditing ? /*#__PURE__*/React.createElement("span", {
    className: classes.editIconSpacer
  }) : null), /*#__PURE__*/React.createElement("div", {
    className: classes.lineItemsBody
  }, lineItems.map((line, index) => {
    var _ref2, _line$description, _line$amount, _line$amount2;
    return /*#__PURE__*/React.createElement("div", {
      className: classes.lineItemRow,
      key: "line-".concat(index)
    }, isEditing ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InputBase, {
      className: "".concat(classes.editInput, " ").concat(classes.lineItemEditName),
      onChange: event => patchLine(index, {
        description: event.target.value
      }),
      placeholder: "Line item",
      value: (_ref2 = (_line$description = line.description) !== null && _line$description !== void 0 ? _line$description : line.category) !== null && _ref2 !== void 0 ? _ref2 : ''
    }), showGlCode ? /*#__PURE__*/React.createElement("span", {
      className: "".concat(classes.lineItemGlCode, " ").concat(classes.lineItemReadOnly)
    }, glCode || 'GL code') : null, /*#__PURE__*/React.createElement(InputBase, {
      className: "".concat(classes.editInput, " ").concat(classes.lineItemEditAmount),
      onChange: event => patchLine(index, {
        amount: event.target.value
      }),
      placeholder: "0.00",
      startAdornment: /*#__PURE__*/React.createElement("span", {
        className: classes.lineItemAdornment
      }, "$"),
      value: (_line$amount = line.amount) !== null && _line$amount !== void 0 ? _line$amount : ''
    }), /*#__PURE__*/React.createElement("button", {
      "aria-label": "Remove this line item",
      className: classes.editIconButton,
      onClick: () => onChange(removeLineItem(data, propertyIndex, serviceIndex, index)),
      type: "button"
    }, /*#__PURE__*/React.createElement(Icon, {
      state: "grey",
      type: "delete"
    }))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: classes.lineItemName
    }, line.description || line.category || DASH), showGlCode ? /*#__PURE__*/React.createElement("span", {
      className: classes.lineItemGlCode
    }, glCode || DASH) : null, /*#__PURE__*/React.createElement("span", {
      className: classes.lineItemAmount
    }, formatMoney((_line$amount2 = line.amount) !== null && _line$amount2 !== void 0 ? _line$amount2 : null, currency))));
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.lineItemRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.lineItemTotalLabel
  }, "Total"), showGlCode ? /*#__PURE__*/React.createElement("span", {
    className: classes.lineItemGlCode
  }) : null, /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.lineItemTotalAmount, " ").concat(isEditing ? classes.lineItemEditAmount : '')
  }, formatMoney(getServiceTotal(service), currency)), isEditing ? /*#__PURE__*/React.createElement("span", {
    className: classes.editIconSpacer
  }) : null)), isEditing ? /*#__PURE__*/React.createElement("div", {
    className: classes.editAddRow
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => onChange(addLineItem(data, propertyIndex, serviceIndex)),
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      state: "dark",
      type: "plus"
    }),
    variant: "outlined"
  }, "Add line item")) : null, isEditing ? /*#__PURE__*/React.createElement("div", {
    className: classes.usageRow
  }, /*#__PURE__*/React.createElement("span", null, "Usage:"), /*#__PURE__*/React.createElement(InputBase, {
    className: "".concat(classes.editInput, " ").concat(classes.lineItemEditAmount),
    endAdornment: /*#__PURE__*/React.createElement("span", {
      className: classes.lineItemAdornment
    }, (_pickValue = pickValue(service, ['usageUnit', 'unit', 'uom'])) !== null && _pickValue !== void 0 ? _pickValue : ''),
    onChange: event => onChange(updateService(data, propertyIndex, serviceIndex, {
      usage: event.target.value
    })),
    placeholder: "0",
    value: (_pickValue2 = pickValue(service, ['usage', 'totalUsage', 'consumption', 'quantity'])) !== null && _pickValue2 !== void 0 ? _pickValue2 : ''
  })) : null, !isEditing && usage ? /*#__PURE__*/React.createElement("div", {
    className: classes.usageRow
  }, /*#__PURE__*/React.createElement("span", null, "Usage:"), /*#__PURE__*/React.createElement("span", {
    className: classes.usageValue
  }, usage)) : null))) : null);
}